import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Botao, Cartao, CartaoTopo, Selo, Vazio, Campo, Entrada, Modal, Esqueleto,
  CAMPO_CODIGO, CAMPO_SEGREDO,
} from "@notes/ui";
import {
  Plug, ArrowLeft, ExternalLink, Unplug, ArrowDownToLine, ArrowUpFromLine, Mic, BarChart3,
  ListChecks, MessageSquareWarning, UserRound, DollarSign, ClipboardList, StickyNote,
  Activity, RefreshCw, SlidersHorizontal, Inbox, Send,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Pagina, Secao, Numeros, Carregando, Erro } from "../shell/Shell.js";
import { usarPonte, usarRelatorioDaPonte, trpc, mensagemDeErro, abrirNoCrm } from "../lib/api.js";
import { dataHora } from "../lib/formato.js";
import { nomeDoEvento } from "../lib/eventos.js";

/**
 * A PONTE COM O CRM.
 *
 * A tela responde, nesta ordem: está ligada, está passando, o que passou, e só
 * então o que fazer se algo estiver errado. O formulário de pareamento vive
 * DENTRO das configurações avançadas quando o par existe: deixá-lo aberto no
 * meio de uma ponte saudável é convidar a refazer o que está funcionando, e foi
 * exatamente o que a tela fazia. É o mesmo desenho do EnturOS MKT.
 *
 * A credencial nunca volta para cá. A pergunta que a tela responde é "está
 * configurada?", jamais "qual é".
 */
export function ConfigPonte() {
  const q = usarPonte();
  const qc = useQueryClient();
  const [form, setForm] = useState({ crmBaseUrl: "https://crm.enturos.com", linkCode: "", segredo: "" });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [desligar, setDesligar] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const [liberados, setLiberados] = useState<number | null>(null);

  const p = q.data?.ponte;
  const ligada = p?.status === "ativo" && p.temSegredo;

  if (q.isLoading) return <Pagina titulo="EnturOS CRM"><Carregando /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="EnturOS CRM"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }

  const recarregar = () => qc.invalidateQueries({ queryKey: ["ponte"] });

  const parear = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null); setSalvando(true);
    try {
      const r = await trpc.agencia.parearComOCrm.mutate({
        crmBaseUrl: form.crmBaseUrl.trim(), linkCode: form.linkCode.trim(), segredo: form.segredo,
      });
      if (!r.ok) { setErro(r.motivo); return; }
      setForm({ ...form, linkCode: "", segredo: "" });
      await recarregar();
    } catch (e2) { setErro(mensagemDeErro(e2)); }
    finally { setSalvando(false); }
  };

  const revogar = async () => {
    setDesligando(true);
    try { await trpc.agencia.revogarPonte.mutate(); setDesligar(false); await recarregar(); }
    catch (e2) { setErro(mensagemDeErro(e2)); }
    finally { setDesligando(false); }
  };

  const tentarAgora = async () => {
    try {
      const r = await trpc.agencia.tentarFilaAgora.mutate();
      setLiberados(r.liberados);
      await recarregar();
    } catch (e2) { setErro(mensagemDeErro(e2)); }
  };

  const formularioDePareamento = (
    <form onSubmit={parear} className="enotes-form">
      <Campo rotulo="Endereço do CRM" htmlFor="crmBaseUrl">
        <Entrada id="crmBaseUrl" name="enderecoDoCrm" type="url" required autoComplete="off"
          value={form.crmBaseUrl}
          onChange={(e) => setForm({ ...form, crmBaseUrl: e.target.value })} />
      </Campo>
      <Campo rotulo="Código de pareamento" htmlFor="linkCode">
        {/* `CAMPO_CODIGO` e `CAMPO_SEGREDO` existem porque o navegador lia
            estes dois campos como usuário e senha e preenchia a conta do site.
            Ver a nota em `@notes/ui`. */}
        <Entrada id="linkCode" name="codigoDePareamento" required {...CAMPO_CODIGO} className="campo num"
          value={form.linkCode} onChange={(e) => setForm({ ...form, linkCode: e.target.value })}
          placeholder="o código que o CRM mostrou" />
      </Campo>
      <Campo rotulo="Segredo compartilhado" htmlFor="segredo"
        dica="Guardado cifrado, e nunca aparece de volta nesta tela. Refazer mantém o anterior válido por quinze minutos, para nenhum evento em trânsito se perder."
        erro={erro && salvando === false && form.segredo ? erro : null}>
        <Entrada id="segredo" name="segredoDoPar" type="password" required minLength={32} {...CAMPO_SEGREDO}
          value={form.segredo} onChange={(e) => setForm({ ...form, segredo: e.target.value })}
          placeholder="o segredo que o CRM mostrou uma vez" />
      </Campo>
      <div>
        <Botao variante="primario" ocupado={salvando}>{ligada ? "Refazer pareamento" : "Ligar ao CRM"}</Botao>
      </div>
    </form>
  );

  return (
    <Pagina
      titulo="EnturOS CRM"
      descricao="A ligação entre as conversas e a negociação."
      acao={
        <Link to="/configuracoes"><Botao tamanho="sm" variante="fantasma"><ArrowLeft size={14} />Configurações</Botao></Link>
      }
    >
      <Secao ordem={0}>
        {p ? (
          <Cartao>
            <CartaoTopo
              icone={<Plug size={15} />}
              titulo={ligada
                ? <>Ligado{p.crmTenantNome ? <> a <strong>{p.crmTenantNome}</strong></> : null}</>
                : p.status === "revogado" ? "Desligado" : `Pareamento ${p.status}`}
              descricao={ligada
                ? "As reuniões analisadas viram histórico na negociação, e o que muda no CRM chega aqui. Sem ação necessária."
                : "Gere um novo código no CRM para religar."}
              acao={
                <Selo tom={ligada ? "bom" : "critico"}>
                  <span aria-hidden className={`sinal ${ligada ? "sinal-bom" : "sinal-critico"}`} />
                  {ligada ? "ativa" : p.status}
                </Selo>
              }
            />
            <dl className="enotes-dados">
              <div><dt>Empresa no CRM</dt><dd className="num">{p.crmTenantId}</dd></div>
              <div><dt>Endereço</dt><dd>{p.crmBaseUrl}</dd></div>
              <div><dt>Credencial</dt><dd>{p.temSegredo ? `configurada (versão ${p.segredoVersao})` : "não configurada"}</dd></div>
              <div><dt>Última entrada</dt><dd>{p.ultimoEntradaEm ? dataHora(p.ultimoEntradaEm) : "nenhuma ainda"}</dd></div>
              <div><dt>Última saída</dt><dd>{p.ultimoSaidaEm ? dataHora(p.ultimoSaidaEm) : "nenhuma ainda"}</dd></div>
            </dl>

            {p.circuitoAbertoAte && (
              <p className="enotes-aviso" role="status">
                <span aria-hidden className="sinal sinal-atencao" />
                Envios pausados até {dataHora(p.circuitoAbertoAte)} depois de {p.falhasSeguidas} falhas seguidas.
                O circuito reabre sozinho.
              </p>
            )}

            {ligada && (
              <div className="enotes-acoes" style={{ marginTop: "1rem" }}>
                <Botao variante="primario" tamanho="sm"
                  onClick={() => void abrirNoCrm({ tipo: "home" }).catch((e) => setErro(mensagemDeErro(e)))}>
                  <ExternalLink size={14} /> Abrir o CRM
                </Botao>
              </div>
            )}
          </Cartao>
        ) : (
          <Vazio
            titulo="Ainda não ligado ao CRM"
            descricao="Com a ligação ativa, o resumo vira histórico na negociação, o botão CRM abre o irmão sem novo login e a proposta enviada fecha a promessa sozinha."
            icone={<Plug size={26} />}
          />
        )}
        {erro && !salvando && <Erro mensagem={erro} />}
      </Secao>

      {ligada
        ? <RelatorioDaPonte aoTentarAgora={tentarAgora} liberados={liberados} />
        : (
          <Secao titulo="Ligar ao CRM" ordem={1}>
            <Cartao>
              <CartaoTopo
                titulo="Código de pareamento"
                descricao={<>No CRM, abra <strong>Configurações › Integrações › EnturOS Notes</strong> e clique em <strong>Gerar código de pareamento</strong>. Cole aqui os dois valores antes de dez minutos.</>}
              />
              {formularioDePareamento}
            </Cartao>
          </Secao>
        )}

      <Secao titulo="O que atravessa a ponte" ordem={3}>
        <div className="enotes-duas-colunas">
          <Cartao>
            <CartaoTopo icone={<ArrowUpFromLine size={15} />} titulo="O Notes manda"
              descricao="Inteligência da conversa" />
            <ul className="enotes-fluxo">
              <li><Mic size={14} aria-hidden /><span><strong>Reuniões analisadas</strong> com resumo e duração</span></li>
              <li><BarChart3 size={14} aria-hidden /><span><strong>Nota da conversa</strong> por dimensão do playbook</span></li>
              <li><ListChecks size={14} aria-hidden /><span><strong>Promessas</strong>, com prazo e aviso quando vencem</span></li>
              <li><MessageSquareWarning size={14} aria-hidden /><span><strong>Objeções, concorrentes e risco</strong></span></li>
              <li><UserRound size={14} aria-hidden /><span><strong>Perfil do viajante</strong> dito na conversa</span></li>
            </ul>
          </Cartao>
          <Cartao>
            <CartaoTopo icone={<ArrowDownToLine size={15} />} titulo="O CRM manda"
              descricao="Contexto comercial" />
            <ul className="enotes-fluxo">
              <li><UserRound size={14} aria-hidden /><span><strong>Contatos</strong>, para reconhecer quem está na sala</span></li>
              <li><DollarSign size={14} aria-hidden /><span><strong>Negociações</strong>, etapa, ganho e perda</span></li>
              <li><ClipboardList size={14} aria-hidden /><span><strong>Tarefas concluídas</strong>, que fecham promessa</span></li>
              <li><StickyNote size={14} aria-hidden /><span><strong>Propostas enviadas</strong>, que fecham a promessa de enviar</span></li>
            </ul>
          </Cartao>
        </div>
      </Secao>

      {ligada && (
        <Secao ordem={4}>
          {/* `details` e não um estado nosso: fecha sozinho, abre com teclado,
              e o que está fechado não distrai quem só veio conferir a saúde. */}
          <details className="enotes-avancado">
            <summary>
              <SlidersHorizontal size={15} aria-hidden />
              <span>
                <strong>Configurações avançadas</strong>
                <small>Refazer o pareamento ou desligar a ponte. Só quem administra a agência.</small>
              </span>
            </summary>
            <div className="enotes-avancado-corpo">
              <Cartao>
                <CartaoTopo titulo="Refazer o pareamento"
                  descricao={<>Use quando o CRM pedir uma credencial nova. Gere o código em <strong>Configurações › Integrações › EnturOS Notes</strong> no CRM. O segredo anterior segue valendo por quinze minutos, para nenhum evento em trânsito se perder.</>} />
                {formularioDePareamento}
              </Cartao>
              <Cartao>
                <CartaoTopo icone={<Unplug size={15} />} titulo="Desligar a ponte"
                  descricao="As reuniões deixam de chegar às negociações e o CRM deixa de avisar o Notes. O histórico já recebido continua aqui." />
                <div className="enotes-acoes">
                  <Botao variante="perigo" tamanho="sm" onClick={() => setDesligar(true)}>
                    <Unplug size={14} /> Desligar
                  </Botao>
                </div>
              </Cartao>
            </div>
          </details>
        </Secao>
      )}

      <Modal
        aberto={desligar}
        aoFechar={() => setDesligar(false)}
        titulo="Desligar a ligação com o CRM?"
        descricao="As reuniões deixam de chegar às negociações e o CRM deixa de avisar o Notes. Para religar, é preciso um novo código de pareamento."
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setDesligar(false)}>Manter ligado</Botao>
            <Botao variante="perigo" ocupado={desligando} onClick={() => void revogar()}>Desligar</Botao>
          </>
        }
      />
    </Pagina>
  );
}

/**
 * O MOVIMENTO da ponte, com dado real.
 *
 * Antes esta tela dizia o que a ponte PODERIA carregar. Isso é folheto. O que
 * a pessoa precisa saber quando abre aqui é se está passando alguma coisa,
 * quanto, de quê, e o que está parado.
 */
function RelatorioDaPonte({ aoTentarAgora, liberados }: {
  aoTentarAgora: () => Promise<void>; liberados: number | null;
}) {
  const r = usarRelatorioDaPonte(24);

  if (r.isLoading) {
    return (
      <Secao titulo="Movimento da ponte" ordem={1}>
        <Cartao><Esqueleto linhas={4} /></Cartao>
      </Secao>
    );
  }
  if (r.isError || !r.data) {
    return (
      <Secao titulo="Movimento da ponte" ordem={1}>
        <Erro mensagem={mensagemDeErro(r.error)} aoTentarDeNovo={() => void r.refetch()} />
      </Secao>
    );
  }

  const { saida, entrada } = r.data;
  const paradosDeVerdade = saida.travados + saida.mortos;
  const passando = saida.ultimaHora + entrada.ultimaHora;

  return (
    <>
      <Secao titulo="Movimento da ponte" ordem={1}
        acao={<span className="carimbo">últimas 24 horas</span>}>
        <Numeros itens={[
          { rotulo: "Saíram para o CRM", valor: saida.naJanela, icone: <Send size={13} />,
            nota: saida.ultimaHora > 0 ? `${saida.ultimaHora} na última hora` : "nenhum na última hora" },
          { rotulo: "Chegaram do CRM", valor: entrada.naJanela, icone: <Inbox size={13} />,
            nota: entrada.ultimaHora > 0 ? `${entrada.ultimaHora} na última hora` : "nenhum na última hora" },
          { rotulo: "Esperando na fila", valor: saida.pendentes, icone: <Activity size={13} />,
            alerta: saida.travados > 0,
            nota: saida.travados > 0 ? `${saida.travados} já tentaram e voltaram` : "nada represado" },
          { rotulo: "Sem entrega", valor: saida.mortos, alerta: saida.mortos > 0,
            nota: saida.mortos > 0 ? "desistiram depois de oito tentativas" : "nenhum evento perdido" },
        ]} />

        <Cartao className={paradosDeVerdade > 0 ? "enotes-estado-atencao" : "enotes-estado-bom"}>
          <div className="enotes-estado-linha">
            <span aria-hidden className={`sinal ${paradosDeVerdade > 0 ? "sinal-atencao" : passando > 0 ? "sinal-bom" : "sinal-atencao"}`} />
            <div className="enotes-estado-texto">
              <strong>
                {paradosDeVerdade > 0
                  ? `${paradosDeVerdade} ${paradosDeVerdade === 1 ? "evento parado" : "eventos parados"}`
                  : passando > 0 ? "Passando agora" : "Pronta, e sem movimento na última hora"}
              </strong>
              <span>
                {paradosDeVerdade > 0
                  ? "A fila tenta sozinha, com espera crescente. Se o outro lado já voltou, peça para tentar agora."
                  : passando > 0
                    ? `${passando} ${passando === 1 ? "evento atravessou" : "eventos atravessaram"} na última hora, nos dois sentidos.`
                    : "Nada atravessou na última hora. É o esperado fora do horário de atendimento."}
              </span>
            </div>
            {saida.travados > 0 && (
              <Botao tamanho="sm" variante="fantasma" onClick={() => void aoTentarAgora()}>
                <RefreshCw size={13} /> Tentar agora
              </Botao>
            )}
          </div>
          {liberados !== null && (
            <p className="enotes-dica-inline" role="status">
              {liberados === 0
                ? "Nada estava esperando: a fila já ia sair na próxima volta."
                : `${liberados} ${liberados === 1 ? "evento liberado" : "eventos liberados"} para a próxima volta, que acontece em segundos.`}
            </p>
          )}
        </Cartao>
      </Secao>

      <Secao titulo="Por tipo de evento" ordem={2}>
        <div className="enotes-duas-colunas">
          <Cartao>
            <CartaoTopo icone={<ArrowUpFromLine size={15} />} titulo="O Notes mandou"
              descricao={saida.naJanela > 0 ? `${saida.naJanela} em 24 horas` : "nada em 24 horas"} />
            <ListaDeEventos itens={saida.porEvento} vazio="O Notes ainda não mandou nada. A primeira reunião analisada abre esta lista." />
          </Cartao>
          <Cartao>
            <CartaoTopo icone={<ArrowDownToLine size={15} />} titulo="O CRM mandou"
              descricao={entrada.naJanela > 0 ? `${entrada.naJanela} em 24 horas` : "nada em 24 horas"} />
            <ListaDeEventos itens={entrada.porEvento} vazio="O CRM ainda não mandou nada nas últimas 24 horas." />
          </Cartao>
        </div>
      </Secao>
    </>
  );
}

function ListaDeEventos({ itens, vazio }: {
  itens: Array<{ evento: string; total: number; ultimo: string | Date | null }>; vazio: string;
}) {
  if (itens.length === 0) return <p className="enotes-vazio-curto">{vazio}</p>;
  const maior = Math.max(...itens.map((i) => i.total));
  return (
    <ul className="enotes-eventos">
      {itens.map((i) => (
        <li key={i.evento}>
          <span className="enotes-evento-nome">{nomeDoEvento(i.evento)}</span>
          {/* A barra é proporcional ao maior da lista: o que importa aqui é
              qual evento domina o tráfego, não o número absoluto. */}
          <span className="enotes-evento-barra" aria-hidden>
            <i style={{ width: `${Math.max(4, Math.round((i.total / maior) * 100))}%` }} />
          </span>
          <span className="enotes-evento-total num">{i.total}</span>
          <span className="carimbo enotes-evento-quando">{i.ultimo ? dataHora(i.ultimo) : "sem registro"}</span>
        </li>
      ))}
    </ul>
  );
}
